import type { ApprovalProposalListItem, RiskLevel } from "@adpilot/shared";
import { IconAlertCircle, IconClockHour4 } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ApprovalEmptyIllustration } from "@/components/approval/approval-empty-illustration";
import { ApprovalProposalTable } from "@/components/approval/approval-proposal-table";
import { Alert, AlertAction, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { useShopScope } from "@/hooks/use-shop-scope";
import { getApiErrorMessage } from "@/lib/api-error";
import { canAccessApprovalQueue } from "@/lib/approval-access";
import { bulkApproveProposals, fetchApprovalProposals } from "@/lib/approval-api";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { useAuthStore } from "@/stores/auth";

const RISK_PRIORITY: Record<RiskLevel, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

/**
 * The review queue is operational, not archival: the nearest deadline wins,
 * then risk breaks ties so reviewers see the most consequential plan first.
 */
export function prioritizeApprovalProposals(
  proposals: readonly ApprovalProposalListItem[],
): ApprovalProposalListItem[] {
  return [...proposals].sort((left, right) => {
    const leftDeadline = left.expiresAt
      ? new Date(left.expiresAt).getTime()
      : Number.POSITIVE_INFINITY;
    const rightDeadline = right.expiresAt
      ? new Date(right.expiresAt).getTime()
      : Number.POSITIVE_INFINITY;
    if (leftDeadline !== rightDeadline) return leftDeadline - rightDeadline;

    const riskDifference =
      (right.highestRisk ? RISK_PRIORITY[right.highestRisk] : 0) -
      (left.highestRisk ? RISK_PRIORITY[left.highestRisk] : 0);
    if (riskDifference !== 0) return riskDifference;
    return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
  });
}

/**
 * 待审批分区（ADP-127）：页面首屏优先展示需要人工处理的审批项,运行历史退居
 * 次要位置。与 sidebar 角标同一数据源(approvals summary.pending),数量天然一致。
 */
export function PendingApprovalsSection({
  managedGroupId,
  variant = "default",
}: {
  managedGroupId?: string;
  variant?: "default" | "managed-group";
}): React.ReactElement | null {
  const { t } = useTranslation("aiOperations");
  const navigate = useNavigate();
  const shops = useAuthStore((state) => state.shops);
  // ADP-127: 与 sidebar 角标同一 shopIds 范围与查询语义,数量天然一致;
  // 聚合视角下跨店列出待审批方案。
  const { scopeShopIds } = useShopScope();
  const queryClient = useQueryClient();
  const canReview = scopeShopIds.some((shopId) => canAccessApprovalQueue(shops, shopId));
  const [now, setNow] = useState(() => new Date());
  const [selectedProposalIds, setSelectedProposalIds] = useState<Set<string>>(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const pendingQuery = useQuery({
    queryKey: ["approvals", "pending-section", scopeShopIds, managedGroupId],
    queryFn: () =>
      fetchApprovalProposals({
        ...DEFAULT_APPROVAL_SEARCH,
        reviewStatus: "pending",
        shopIds: scopeShopIds.join(","),
        managedGroupId,
      }),
    enabled: canReview && scopeShopIds.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (!canReview) return;
    const intervalId = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(intervalId);
  }, [canReview]);

  const proposals = useMemo(
    () => prioritizeApprovalProposals(pendingQuery.data?.data ?? []),
    [pendingQuery.data?.data],
  );
  const pendingCount = pendingQuery.data?.summary.pending ?? 0;
  const expiringSoonCount = pendingQuery.data?.summary.expiringSoon ?? 0;
  const selectedProposals = proposals.filter(({ id }) => selectedProposalIds.has(id));
  const bulkMutation = useMutation({
    mutationFn: () =>
      bulkApproveProposals(
        {
          proposals: selectedProposals.map((proposal) => ({
            proposalId: proposal.id,
            shopId: proposal.shopId!,
            actionIds: proposal.actions
              .filter(({ reviewStatus }) => reviewStatus === "pending")
              .map(({ id }) => id),
          })),
        },
        crypto.randomUUID(),
      ),
    onSuccess: async () => {
      setConfirmOpen(false);
      setSelectedProposalIds(new Set());
      toast.success(t("pending.bulk.success", { count: selectedProposals.length }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["approvals"] }),
        queryClient.invalidateQueries({ queryKey: ["ai-operations"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["ai-managed-groups"] }),
      ]);
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });

  useEffect(() => {
    const available = new Set(proposals.map(({ id }) => id));
    setSelectedProposalIds((current) => {
      const next = new Set([...current].filter((id) => available.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [proposals]);

  if (!canReview) return null;

  return (
    <section aria-label={t("pending.title")} className="flex shrink-0 flex-col gap-3">
      {variant === "default" ? (
        <div className="flex min-h-11 items-center justify-between gap-4 rounded-lg border border-line bg-surface px-3.5 py-2.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-warning/10 text-warning-foreground">
              <IconClockHour4 aria-hidden />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-[13px] font-semibold leading-[18px] text-ink">
                  {t("pending.title")}
                </h2>
                {pendingCount > 0 ? (
                  <Badge variant="warning" size="compact">
                    {t("pending.summary", { count: pendingCount })}
                  </Badge>
                ) : null}
              </div>
              <p className="truncate text-[10px] leading-4 text-muted-foreground">
                {t("pending.priorityHint")}
              </p>
            </div>
          </div>
          {expiringSoonCount > 0 ? (
            <Badge
              variant="outline"
              className="shrink-0 border-warning/30 bg-warning/5 text-warning-foreground"
            >
              {t("pending.urgent", { count: expiringSoonCount })}
            </Badge>
          ) : null}
        </div>
      ) : null}
      {pendingQuery.isPending ? (
        <PendingApprovalsSkeleton />
      ) : pendingQuery.isError && !pendingQuery.data ? (
        <Alert variant="error">
          <IconAlertCircle />
          <AlertTitle>{t("pending.loadErrorTitle")}</AlertTitle>
          <AlertDescription>{t("pending.loadErrorDescription")}</AlertDescription>
          <AlertAction>
            <Button variant="outline" size="sm" onClick={() => void pendingQuery.refetch()}>
              {t("common:actions.retry")}
            </Button>
          </AlertAction>
        </Alert>
      ) : proposals.length === 0 ? (
        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          <Empty className="py-8">
            <EmptyMedia>
              <ApprovalEmptyIllustration />
            </EmptyMedia>
            <EmptyHeader>
              <EmptyTitle className="text-base">{t("pending.emptyTitle")}</EmptyTitle>
              <EmptyDescription>{t("pending.emptyDescription")}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {pendingQuery.isError ? (
            <Alert variant="warning">
              <IconAlertCircle />
              <AlertTitle>{t("pending.refreshErrorTitle")}</AlertTitle>
              <AlertDescription>{t("pending.refreshErrorDescription")}</AlertDescription>
            </Alert>
          ) : null}
          {variant === "managed-group" ? (
            <div className="flex min-h-12 items-center justify-between rounded-[10px] border border-line bg-surface px-3.5 py-2">
              <span className="text-xs text-muted-foreground">
                {t("pending.bulk.selected", { count: selectedProposals.length })}
              </span>
              <Button
                size="sm"
                disabled={selectedProposals.length === 0}
                onClick={() => setConfirmOpen(true)}
              >
                {t("pending.bulk.action", { count: selectedProposals.length })}
              </Button>
            </div>
          ) : null}
          <ApprovalProposalTable
            proposals={proposals}
            now={now}
            selectedProposalIds={selectedProposalIds}
            onSelectionChange={(proposalId, selected) =>
              setSelectedProposalIds((current) => {
                const next = new Set(current);
                if (selected) next.add(proposalId);
                else next.delete(proposalId);
                return next;
              })
            }
            onSelectAll={(selected) =>
              setSelectedProposalIds(selected ? new Set(proposals.map(({ id }) => id)) : new Set())
            }
            footer={
              pendingCount > proposals.length ? (
                <div className="px-3.5 py-2 text-[11px] text-muted-foreground">
                  {t("pending.truncated", { shown: proposals.length, total: pendingCount })}
                </div>
              ) : undefined
            }
            onReview={(proposalId) => {
              const item = proposals.find(({ id }) => id === proposalId);
              if (item) {
                void navigate({ to: "/ai-operations/$runId", params: { runId: item.runId } });
              }
            }}
          />
          {variant === "default" && selectedProposals.length > 0 ? (
            <div className="flex items-center justify-between rounded-lg border border-line bg-surface px-3.5 py-2.5">
              <span className="text-xs text-muted-foreground">
                {t("pending.bulk.selected", { count: selectedProposals.length })}
              </span>
              <Button size="sm" onClick={() => setConfirmOpen(true)}>
                {t("pending.bulk.action", { count: selectedProposals.length })}
              </Button>
            </div>
          ) : null}
          <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <DialogPopup>
              <DialogHeader>
                <DialogTitle>{t("pending.bulk.title")}</DialogTitle>
                <DialogDescription>
                  {t("pending.bulk.description", { count: selectedProposals.length })}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                  {t("common:actions.cancel")}
                </Button>
                <Button
                  loading={bulkMutation.isPending}
                  disabled={selectedProposals.length === 0}
                  onClick={() => bulkMutation.mutate()}
                >
                  {t("pending.bulk.confirm", { count: selectedProposals.length })}
                </Button>
              </DialogFooter>
            </DialogPopup>
          </Dialog>
        </div>
      )}
    </section>
  );
}

function PendingApprovalsSkeleton(): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  return (
    <div
      role="status"
      aria-label={t("pending.loading")}
      className="flex flex-col gap-2 rounded-lg border border-line bg-surface p-3"
    >
      <Skeleton className="h-7 w-full" />
      <Skeleton className="h-14 w-full" />
      <Skeleton className="h-14 w-full" />
    </div>
  );
}
